import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Alert, 
  Typography, 
  Space, 
  Tag, 
  Table, 
  Tabs,
  Progress,
  Skeleton
} from "antd";
import { 
  UserOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import { ANALYTICS_API_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { CohortAnalytics as CohortAnalyticsType } from "../../../types/analytics";
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface CohortAnalyticsProps {
  loading?: boolean;
}

export const CohortAnalytics: React.FC<CohortAnalyticsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<CohortAnalyticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const { filters } = useAnalytics();

  useEffect(() => {
    const fetchCohortAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAndSanitize("token");
        const adminId = getAndSanitize("adminId");

        if (!token || !adminId) {
          setError("Token o adminId no disponible");
          return;
        }

        const queryParams = new URLSearchParams({
          adminId,
          'dateRange[start]': filters.dateRange.start,
          'dateRange[end]': filters.dateRange.end,
          ...(filters.accountName && { accountName: filters.accountName }),
        });

        const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/cohorts?${queryParams}`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Admin-ID": adminId,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setData(result.data);
          setLastUpdated(new Date());
        } else {
          setError(result.message || "Error al obtener datos de cohorts");
        }
      } catch (err) {
        console.error("Error fetching cohort analytics:", err);
        setError(err instanceof Error ? err.message : "Error al cargar analytics de cohorts");
      } finally {
        setLoading(false);
      }
    };

    fetchCohortAnalytics();
  }, [filters]);

  const getRetentionTrendIcon = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'declining':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MinusOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getRetentionTrendColor = (trend: 'improving' | 'declining' | 'stable') => {
    switch (trend) {
      case 'improving':
        return '#52c41a';
      case 'declining':
        return '#ff4d4f';
      default:
        return '#faad14';
    }
  };

  // Preparar datos para el gráfico de retención
  const retentionChartData = data?.retentionByWeek.map(cohort => ({
    cohort: cohort.cohortWeek,
    'Semana 1': cohort.retentionRateWeek1,
    'Semana 2': cohort.retentionRateWeek2,
    'Semana 4': cohort.retentionRateWeek4,
    'Semana 8': cohort.retentionRateWeek8,
    'Semana 12': cohort.retentionRateWeek12,
    size: cohort.cohortSize
  })) || [];

  // Preparar datos para el gráfico de LTV
  const ltvChartData = data?.lifetimeValue.map(ltv => ({
    cohort: ltv.cohort,
    'LTV Actual': ltv.averageLTV,
    'LTV Proyectado': ltv.projectedLTV,
    'Ingresos Totales': ltv.totalRevenue,
    'Días Promedio': ltv.averageLifespanDays
  })) || [];

  // Columnas para la tabla de retención
  const retentionColumns = [
    {
      title: 'Cohorte',
      dataIndex: 'cohortWeek',
      key: 'cohortWeek',
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Tamaño',
      dataIndex: 'cohortSize',
      key: 'cohortSize',
      render: (value: number) => <Text strong>{value.toLocaleString()}</Text>
    },
    {
      title: 'Sem 1',
      dataIndex: 'retentionRateWeek1',
      key: 'week1',
      render: (value: number) => (
        <Progress 
          percent={value} 
          size="small" 
          format={percent => `${percent?.toFixed(1)}%`}
          strokeColor={value > 70 ? '#52c41a' : value > 50 ? '#faad14' : '#ff4d4f'}
        />
      )
    },
    {
      title: 'Sem 2',
      dataIndex: 'retentionRateWeek2',
      key: 'week2',
      render: (value: number) => (
        <Progress 
          percent={value} 
          size="small" 
          format={percent => `${percent?.toFixed(1)}%`}
          strokeColor={value > 60 ? '#52c41a' : value > 40 ? '#faad14' : '#ff4d4f'}
        />
      )
    },
    {
      title: 'Sem 4',
      dataIndex: 'retentionRateWeek4',
      key: 'week4',
      render: (value: number) => (
        <Progress 
          percent={value} 
          size="small" 
          format={percent => `${percent?.toFixed(1)}%`}
          strokeColor={value > 50 ? '#52c41a' : value > 30 ? '#faad14' : '#ff4d4f'}
        />
      )
    },
    {
      title: 'Sem 8',
      dataIndex: 'retentionRateWeek8',
      key: 'week8',
      render: (value: number) => (
        <Progress 
          percent={value} 
          size="small" 
          format={percent => `${percent?.toFixed(1)}%`}
          strokeColor={value > 40 ? '#52c41a' : value > 20 ? '#faad14' : '#ff4d4f'}
        />
      )
    },
    {
      title: 'Sem 12',
      dataIndex: 'retentionRateWeek12',
      key: 'week12',
      render: (value: number) => (
        <Progress 
          percent={value} 
          size="small" 
          format={percent => `${percent?.toFixed(1)}%`}
          strokeColor={value > 30 ? '#52c41a' : value > 15 ? '#faad14' : '#ff4d4f'}
        />
      )
    }
  ];

  if (loading || propLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar Análisis de Cohorts"
        description={error}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos disponibles"
        description="No hay suficientes datos para generar el análisis de cohorts"
        type="info"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>📊 Análisis de Cohorts de Usuarios</Title>
          <Text type="secondary">
            Análisis de retención de usuarios agrupados por semana de registro
          </Text>
        </div>

        {/* Métricas Principales */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Cohortes Analizados"
                value={data.retentionByWeek.length}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Retención Promedio (Sem 4)"
                value={data.cohortComparison.averageRetentionWeek4}
                suffix="%"
                precision={1}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Mejor Cohorte"
                value={data.cohortComparison.bestPerformingCohort}
                prefix={<TrophyOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Tendencia de Retención"
                value={data.cohortComparison.retentionTrend === 'improving' ? 'Mejorando' : 
                       data.cohortComparison.retentionTrend === 'declining' ? 'Declinando' : 'Estable'}
                prefix={getRetentionTrendIcon(data.cohortComparison.retentionTrend)}
                valueStyle={{ color: getRetentionTrendColor(data.cohortComparison.retentionTrend) }}
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos y Tablas */}
        <Tabs defaultActiveKey="retention" size="large">
          <TabPane tab="📈 Curvas de Retención" key="retention">
            <Card title="Retención por Cohorte y Semana">
              <div style={{ padding: 20 }}>
                <Text strong>Visualización de retención por cohortes</Text>
                <br />
                <Text type="secondary">Los datos detallados están disponibles en la tabla</Text>
                <div style={{ marginTop: 20 }}>
                  {retentionChartData.slice(0, 5).map((cohort) => (
                    <div key={cohort.cohort} style={{ marginBottom: 16 }}>
                      <Text strong>{cohort.cohort}</Text>
                      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col span={4}>
                          <Progress 
                            percent={cohort['Semana 1']} 
                            size="small" 
                            strokeColor="#1890ff"
                            format={() => `Sem 1: ${cohort['Semana 1'].toFixed(1)}%`}
                          />
                        </Col>
                        <Col span={4}>
                          <Progress 
                            percent={cohort['Semana 2']} 
                            size="small" 
                            strokeColor="#52c41a"
                            format={() => `Sem 2: ${cohort['Semana 2'].toFixed(1)}%`}
                          />
                        </Col>
                        <Col span={4}>
                          <Progress 
                            percent={cohort['Semana 4']} 
                            size="small" 
                            strokeColor="#faad14"
                            format={() => `Sem 4: ${cohort['Semana 4'].toFixed(1)}%`}
                          />
                        </Col>
                        <Col span={4}>
                          <Progress 
                            percent={cohort['Semana 8']} 
                            size="small" 
                            strokeColor="#ff7875"
                            format={() => `Sem 8: ${cohort['Semana 8'].toFixed(1)}%`}
                          />
                        </Col>
                        <Col span={4}>
                          <Progress 
                            percent={cohort['Semana 12']} 
                            size="small" 
                            strokeColor="#722ed1"
                            format={() => `Sem 12: ${cohort['Semana 12'].toFixed(1)}%`}
                          />
                        </Col>
                        <Col span={4}>
                          <Text type="secondary">Tamaño: {cohort.size}</Text>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="💰 Valor de Vida (LTV)" key="ltv">
            <Card title="Lifetime Value por Cohorte">
              <div style={{ padding: 20 }}>
                <Row gutter={[16, 16]}>
                  {ltvChartData.slice(0, 6).map((ltv) => (
                    <Col xs={24} sm={12} lg={8} key={ltv.cohort}>
                      <Card size="small" title={ltv.cohort}>
                        <Statistic
                          title="LTV Actual"
                          value={ltv['LTV Actual']}
                          prefix="$"
                          valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                        />
                        <Statistic
                          title="LTV Proyectado"
                          value={ltv['LTV Proyectado']}
                          prefix="$"
                          valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                          style={{ marginTop: 8 }}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">
                            Días activos: {Math.round(ltv['Días Promedio'])}
                          </Text>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="📋 Tabla Detallada" key="table">
            <Card title="Datos de Retención por Cohorte">
              <Table
                dataSource={data.retentionByWeek}
                columns={retentionColumns}
                rowKey="cohortWeek"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 800 }}
              />
            </Card>
          </TabPane>

          <TabPane tab="💎 Valor por Cohorte" key="value">
            <Card title="Análisis de Valor por Cohorte">
              <Table
                dataSource={data.lifetimeValue}
                columns={[
                  {
                    title: 'Cohorte',
                    dataIndex: 'cohort',
                    key: 'cohort',
                    render: (text: string) => <Tag color="purple">{text}</Tag>
                  },
                  {
                    title: 'LTV Promedio',
                    dataIndex: 'averageLTV',
                    key: 'averageLTV',
                    render: (value: number) => (
                      <Text strong>${value.toLocaleString()}</Text>
                    ),
                    sorter: (a, b) => a.averageLTV - b.averageLTV
                  },
                  {
                    title: 'LTV Proyectado',
                    dataIndex: 'projectedLTV',
                    key: 'projectedLTV',
                    render: (value: number) => (
                      <Text style={{ color: '#52c41a' }}>${value.toLocaleString()}</Text>
                    ),
                    sorter: (a, b) => a.projectedLTV - b.projectedLTV
                  },
                  {
                    title: 'Días Promedio Activo',
                    dataIndex: 'averageLifespanDays',
                    key: 'averageLifespanDays',
                    render: (value: number) => (
                      <Text>{Math.round(value)} días</Text>
                    ),
                    sorter: (a, b) => a.averageLifespanDays - b.averageLifespanDays
                  },
                  {
                    title: 'Ingresos Totales',
                    dataIndex: 'totalRevenue',
                    key: 'totalRevenue',
                    render: (value: number) => (
                      <Text strong style={{ color: '#1890ff' }}>
                        ${value.toLocaleString()}
                      </Text>
                    ),
                    sorter: (a, b) => a.totalRevenue - b.totalRevenue
                  }
                ]}
                rowKey="cohort"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </TabPane>
        </Tabs>

        {/* Footer con información */}
        <Card size="small">
          <Text type="secondary">
            📅 Última actualización: {lastUpdated.toLocaleString('es-ES')} | 
            💡 Los cohortes se agrupan por semana de registro del usuario
          </Text>
        </Card>
      </Space>
    </div>
  );
};

export default CohortAnalytics; 